import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AiOutlineCalendar, AiOutlineDashboard, AiOutlineFile } from 'react-icons/ai';
import { FaSignOutAlt } from 'react-icons/fa';
import talentLogo3 from '../assets/talentlogo3-removebg-preview.png';
import { BASE_URL } from '../config';
import useAuth from '../hooks/useAuth';
import { resolveBackendUrl } from '../utils/accountAvatarUrl';
import { useClientSignoffStatsContext } from './ClientSignoffStatsContext';

const ClientSidebar = ({ onLinkClick, fullWidth = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const signoffStats = useClientSignoffStatsContext();
  const [profilePicture, setProfilePicture] = useState("");
  const [userName, setUserName] = useState("");
  
  const menuItems = [
    {
      path: '/client-dashboard',
      label: 'Dashboard',
      icon: <AiOutlineDashboard size={19} />,
      badge: signoffStats.total > 0 ? signoffStats.total : null,
    },
    { path: '/client-dashboard/shifts/schedule', label: 'Shifts', icon: <AiOutlineCalendar size={19} /> },
    { path: '/client-dashboard/files', label: 'Files', icon: <AiOutlineFile size={19} /> },
  ];

  // Fetch profile picture
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const accountId = auth?.accountId || localStorage.getItem("accountId");
        const token = auth?.token || localStorage.getItem("token");
        if (!accountId) return;

        const res = await fetch(`${BASE_URL}/api/my-account/account/me`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        });

        const json = await res.json();
        if (json.success && json.data) {
          const d = json.data;
          setProfilePicture(resolveBackendUrl(d.profile_picture));
          const fn = d.first_name || "";
          const ln = d.last_name || "";
          setUserName(`${fn} ${ln}`.trim() || "Client contact");
        }
      } catch (e) {
        console.error("Failed to load profile", e);
      }
    };

    if (auth?.accountId || localStorage.getItem("accountId")) {
      loadProfile();
    }
  }, [auth?.accountId]);

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (userName) {
      const parts = userName.split(" ").filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return parts[0]?.slice(0, 2).toUpperCase() || "CX";
    }
    return "CX";
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("accountId");
    localStorage.removeItem("clientId");
    localStorage.removeItem("userType");
    localStorage.removeItem("isAdmin");
    window.dispatchEvent(new Event('authUpdate'));
    navigate("/login");
  };

  const containerClass = fullWidth ? 'w-full bg-sidebar-bg text-sidebar-text flex flex-col h-screen sticky top-0 overflow-y-auto' : 'w-60 bg-sidebar-bg text-sidebar-text flex flex-col h-screen sticky top-0 overflow-y-auto';

  return (
    <div className={containerClass}>
      <div className="p-6">
          <div className="flex items-center justify-center">
            <img src={talentLogo3} alt="Talent Logo" className="h-14 w-auto" />
          </div>
      </div>

      <nav>
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => onLinkClick && onLinkClick()}
            className={`w-full flex items-center px-4 py-3 mb-1 rounded-lg transition-colors ${
              item.path === '/client-dashboard/shifts/schedule'
                ? location.pathname.startsWith('/client-dashboard/shifts')
                  ? 'bg-sidebar-active text-white'
                  : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
                : location.pathname === item.path
                  ? 'bg-sidebar-active text-white'
                  : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
            }`}
          >
            <span className="text-sm font-medium ml-2 mr-4 text-sidebar-text">{item.icon}</span>
            <span className="flex-1">{item.label}</span>
            {item.badge ? (
              <span className="ml-auto mr-2 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center">
                {item.badge}
              </span>
            ) : null}
          </Link>
        ))}
      </nav>

      <div className="p-4 border-t border-sidebar-hover mt-auto">
        <Link to="/client-dashboard/my-account" onClick={() => onLinkClick && onLinkClick()}>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center overflow-hidden">
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextElementSibling.style.display = "flex";
                  }}
                />
              ) : null}
              <span className={`text-white text-xs font-medium ${profilePicture ? "hidden" : ""}`}>
                {getUserInitials()}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm text-sidebar-text-muted">My Account</span>
              <span className="text-xs text-sidebar-text-muted">{userName || "Client contact"}</span>
            </div>
          </div>
        </Link>
        <button
          onClick={handleLogout}
          className="flex items-center px-4 py-2 mt-2 text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text rounded-lg transition-colors w-full"
        >
          <FaSignOutAlt className="mr-2 font-medium text-sidebar-text-muted" size={18} />
          <span className="text-sm font-medium">Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default ClientSidebar;
